import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <Alert variant="destructive" className="max-w-3xl">
        <AlertTitle>페이지 렌더링 중 오류가 발생했습니다</AlertTitle>
        <AlertDescription className="space-y-3">
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all font-mono text-xs">
            {error.message}
            {error.stack ? `\n\n${error.stack}` : ''}
          </pre>
          <p className="text-muted-foreground">
            다른 메뉴로 이동하거나 새로고침하세요. 같은 오류가 반복되면 개발팀에 공유해주세요.
          </p>
          <Button variant="outline" size="sm" onClick={this.reset}>
            다시 시도
          </Button>
        </AlertDescription>
      </Alert>
    );
  }
}
